import { ReactNode, ReactElement, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { makeCrossSectionLabel } from '@editorUtils'
import { v4 } from 'uuid'
import { Box, TextField, useTheme } from '@mui/material'
import { CentimeterField, UnitConvertedField } from '@ui/forms'
import { formatCmFromMeters } from '../../../utils'
import { inlineFieldSx } from '../utils'

/** Display-only b/h (cm) for line-like Auflager QS; no form wiring. */
function readRectangularSupportMemberDimsM(
  cs: CrossSectionTimberOrCLTOrSteel | undefined,
): { widthM: number; heightM: number } | null {
  if (!cs) return null
  // Only rectangular timber has freely editable b/h. Steel is defined by its named profile
  // (editing b/h would desync it from the profile) and CLT by its identifier, so those show the
  // read-only cross-section label instead of dimension inputs.
  if (cs.kind === 'Timber' && cs.shape.kind_literal === 'RectangularCS') {
    return { widthM: cs.shape.width, heightM: cs.shape.height }
  }
  return null
}

/** `@ui/forms` CentimeterField with support-grid underline layout (label left of field). */
export const SupportInlineCmField = ({
  name,
  label,
  placeholder,
  inputMaxWidthPx,
  disableForm = false,
}: {
  name: string
  label?: ReactNode
  placeholder?: string
  inputMaxWidthPx?: number
  disableForm?: boolean
}): ReactElement => {
  const theme = useTheme()

  const id = useMemo(() => v4(), [])

  const fieldName = disableForm ? `randomfieldname-${id}` : name

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.35,
        fontSize: 11,
        color: theme.palette.text.secondary,
        ...(inputMaxWidthPx != null ? { alignSelf: 'flex-start', maxWidth: '100%' } : null),
      }}
    >
      {label ? (
        <Box component="span" sx={{ minWidth: 12, textAlign: 'right', flexShrink: 0 }}>
          {label}
        </Box>
      ) : null}
      <CentimeterField
        key={fieldName}
        // this is needed because otherwise the field will be set to
        // null and the area field (e.g. length or width) will then be set to null"
        name={fieldName}
        disabled={disableForm}
        hideFormControl
        hideAdornment
        twoDecimalPlaces
        variant="standard"
        placeholder={placeholder}
        sx={{
          flex: inputMaxWidthPx != null ? 'none' : 1,
          width: inputMaxWidthPx != null ? inputMaxWidthPx : undefined,
          maxWidth: inputMaxWidthPx != null ? inputMaxWidthPx : undefined,
          minWidth: 0,
          ...inlineFieldSx,
        }}
        InputProps={{ sx: { fontSize: 12, fontFamily: 'Roboto' } }}
      />
    </Box>
  )
}

/** `@ui/forms` UnitConvertedField with support-grid underline layout (label left of field). */
export const InlineNumberField = ({
  name,
  label,
  placeholder,
  defaultValue,
  conversionFactor,
  /** Caps input width (px); omit so the field grows with the grid cell (Geom. Auflager / Überstand). */
  inputMaxWidthPx,
  disableForm = false,
}: {
  /** Form field name; when set, uses UnitConvertedField (de-DE number input, empty-field handling). */
  name?: string
  label?: ReactNode
  placeholder?: string
  /** Display-only default when `name` is omitted (read-only QS rows). */
  defaultValue?: string
  conversionFactor?: number
  /** Green bottom border when the field has a value (Geom. Auflager / Überstand). */
  greenUnderlineWhenFilled?: boolean
  inputMaxWidthPx?: number
  disableForm?: boolean
}): ReactElement | null => {
  const theme = useTheme()

  const id = useMemo(() => v4(), [])

  const fieldName = disableForm || name == null ? `randomfieldname-${id}` : name

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.35,
        fontSize: 11,
        color: theme.palette.text.secondary,
        ...(inputMaxWidthPx != null ? { alignSelf: 'flex-start', maxWidth: '100%' } : null),
      }}
    >
      {label ? (
        <Box component="span" sx={{ minWidth: 12, textAlign: 'right', flexShrink: 0 }}>
          {label}
        </Box>
      ) : null}
      {name != null && conversionFactor != null ? (
        <UnitConvertedField
          key={fieldName}
          name={fieldName}
          disabled={disableForm}
          hideFormControl
          hideAdornment
          conversionFactor={conversionFactor}
          variant="standard"
          placeholder={placeholder}
          sx={{
            flex: inputMaxWidthPx != null ? 'none' : 1,
            width: inputMaxWidthPx != null ? inputMaxWidthPx : undefined,
            maxWidth: inputMaxWidthPx != null ? inputMaxWidthPx : undefined,
            minWidth: 0,
            ...inlineFieldSx,
          }}
          InputProps={{ sx: { fontSize: 12, fontFamily: 'Roboto' } }}
        />
      ) : (
        <TextField
          size="small"
          placeholder={placeholder}
          defaultValue={defaultValue}
          disabled={disableForm}
          variant="standard"
          InputProps={{ sx: { fontSize: 12, fontFamily: 'Roboto' } }}
          sx={{
            flex: inputMaxWidthPx != null ? 'none' : 1,
            width: inputMaxWidthPx != null ? inputMaxWidthPx : undefined,
            maxWidth: inputMaxWidthPx != null ? inputMaxWidthPx : undefined,
            minWidth: 0,
            ...inlineFieldSx,
          }}
        />
      )}
    </Box>
  )
}

/** QS-Zeile ohne Form-Anbindung (z.B. wenn das Ziel-Bauteil keinen Eintrag unter `elements` hat). */
export const SupportLineLikeQsFieldsReadOnly = ({
  crossSection,
}: {
  crossSection: CrossSectionTimberOrCLTOrSteel
}): ReactElement => {
  const dims = readRectangularSupportMemberDimsM(crossSection)
  if (!dims) {
    return (
      <Box sx={{ fontSize: 11, color: 'text.secondary', overflowWrap: 'anywhere' }}>
        {makeCrossSectionLabel(crossSection) ?? '—'}
      </Box>
    )
  }

  const bCm = formatCmFromMeters(dims.widthM)
  const hCm = formatCmFromMeters(dims.heightM)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.3,
        width: '100%',
        minWidth: 0,
        alignItems: 'flex-start',
      }}
    >
      <InlineNumberField
        label="b"
        defaultValue={bCm}
        placeholder={formatCmFromMeters(dims.widthM)}
        inputMaxWidthPx={40}
      />
      <InlineNumberField
        label="h"
        defaultValue={hCm}
        placeholder={formatCmFromMeters(dims.heightM)}
        inputMaxWidthPx={40}
      />
    </Box>
  )
}

/**
 * Bearbeitet b/h im Querschnitt eines verbundenen Bauteils im Root-Formular (`elements[i].crossSection`).
 */
export const SupportLineLikeQsFieldsForm = ({
  crossSectionPrefix,
  disableForm = false,
}: {
  crossSectionPrefix: string
  disableForm?: boolean
}): ReactElement => {
  const crossSection = useWatch({
    name: crossSectionPrefix,
  }) as CrossSectionTimberOrCLTOrSteel | null | undefined

  const dims = readRectangularSupportMemberDimsM(crossSection ?? undefined)

  const widthFieldName = `${crossSectionPrefix}.shape.width`
  const heightFieldName = `${crossSectionPrefix}.shape.height`

  if (!dims || crossSection == null) {
    return (
      <Box sx={{ fontSize: 11, color: 'text.secondary', overflowWrap: 'anywhere' }}>
        {crossSection != null ? makeCrossSectionLabel(crossSection) ?? '—' : '—'}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.3,
        width: '100%',
        minWidth: 0,
        alignItems: 'flex-start',
      }}
    >
      <SupportInlineCmField
        name={widthFieldName}
        label="b"
        placeholder={formatCmFromMeters(dims.widthM)}
        inputMaxWidthPx={26}
        disableForm={disableForm}
      />
      <SupportInlineCmField
        name={heightFieldName}
        label="h"
        placeholder={formatCmFromMeters(dims.heightM)}
        inputMaxWidthPx={26}
        disableForm={disableForm}
      />
    </Box>
  )
}
